(function () {
  if (customElements.get('brand-directory')) return;

  const COUNT_CONCURRENCY = 2;
  const CACHE_TTL = 15 * 60 * 1000;
  const CACHE_VERSION = 3;
  const STOREFRONT_ENDPOINT = '/api/2026-07/graphql.json';
  const STOREFRONT_QUERY = `
    query BrandDirectoryCounts($cursor: String, $country: CountryCode)
      @inContext(country: $country) {
      products(first: 250, after: $cursor, sortKey: ID) {
        nodes {
          id
          availableForSale
          brand: metafield(namespace: "custom", key: "brand") {
            type
            value
          }
          sizeRecommend: metafield(namespace: "custom", key: "size_recommend") {
            type
            value
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  class BrandDirectory extends HTMLElement {
    cacheKey(kind) {
      const context = [
        window.Shopify?.theme?.id || 'theme',
        this.dataset.sizeMetafieldKey || 'size_recommend',
        window.Shopify?.country || 'KR',
        window.Shopify?.currency?.active || 'currency',
        globalThis.document?.documentElement?.lang || 'language',
      ].map((value) => encodeURIComponent(String(value))).join(':');
      return `fwd:brand-directory:${kind}:v${CACHE_VERSION}:${context}`;
    }

    readSessionCache(kind) {
      try {
        const cached = JSON.parse(window.sessionStorage.getItem(this.cacheKey(kind)) || 'null');
        if (!cached || Date.now() - cached.cachedAt > CACHE_TTL) return null;
        return cached.value;
      } catch (_error) {
        return null;
      }
    }

    writeSessionCache(kind, value) {
      try {
        window.sessionStorage.setItem(this.cacheKey(kind), JSON.stringify({ cachedAt: Date.now(), value }));
      } catch (_error) {
        // The directory still works when browser storage is unavailable.
      }
    }

    serializeCountResult(result, sizeCatalog) {
      return {
        keyType: result.keyType,
        counts: Array.from(result.counts.entries()).map(([key, count]) => [key, {
          available: count.available,
          total: count.total,
          sizeIds: Array.from(count.sizeIds || []),
          products: Array.from(count.products || []).map((product) => ({
            id: product.id,
            available: Boolean(product.available),
            sizeIds: Array.from(product.sizeIds || []),
          })),
        }]),
        sizeCatalog: Array.from(sizeCatalog.entries()),
      };
    }

    deserializeCountResult(cached) {
      if (!cached
        || !['gid', 'url'].includes(cached.keyType)
        || !Array.isArray(cached.counts)
        || !Array.isArray(cached.sizeCatalog)) return null;
      try {
        const counts = new Map(cached.counts.map(([key, count]) => [key, {
          available: count.available,
          total: count.total,
          sizeIds: new Set(count.sizeIds || []),
          products: (count.products || []).map((product) => ({
            id: product.id,
            available: Boolean(product.available),
            sizeIds: new Set(product.sizeIds || []),
          })),
        }]));
        return {
          result: { keyType: cached.keyType, counts },
          sizeCatalog: new Map(cached.sizeCatalog),
        };
      } catch (_error) {
        return null;
      }
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        this.addSizeFilterDismissListeners();
        return;
      }

      this.dataset.initialized = 'true';
      this.items = Array.from(this.querySelectorAll('[data-brand-item]'));
      this.sortSelect = this.querySelector('[data-brand-sort]');
      this.soldOutToggle = this.querySelector('[data-brand-sold-out-toggle]');
      this.sizeFilter = this.querySelector('[data-brand-size-filter]');
      this.sizeOptions = this.querySelector('[data-brand-size-options]');
      this.sizeCount = this.querySelector('[data-brand-size-count]');
      this.sizeClear = this.querySelector('[data-brand-size-clear]');
      this.activeFilters = this.querySelector('[data-brand-active-filters]');
      this.filterEmpty = this.querySelector('[data-brand-filter-empty]');
      this.selectedSizeIds = new Set();
      this.restoreControlsFromUrl();
      this.sortSelect?.addEventListener('click', () => this.toggleSortMode());
      this.soldOutToggle?.addEventListener('click', () => this.toggleSoldOutBrands());
      this.sizeOptions?.addEventListener('change', (event) => {
        if (event.target.matches('input[type="checkbox"]')) this.updateSizeSelection();
      });
      this.sizeClear?.addEventListener('click', () => this.clearSizeSelection());
      this.activeFilters?.addEventListener('click', (event) => {
        const badge = event.target.closest('[data-brand-size-badge]');
        if (!badge) return;
        const checkbox = Array.from(this.sizeOptions.querySelectorAll('input[type="checkbox"]'))
          .find((input) => input.value === badge.dataset.brandSizeBadge);
        if (checkbox) checkbox.checked = false;
        this.updateSizeSelection();
      });
      this.addSizeFilterDismissListeners();
      this.setCountControlsEnabled(false);

      this.initialize();
    }

    disconnectedCallback() {
      this.directoryRequest?.abort();
      this.countRequest?.abort();
      this.removeSizeFilterDismissListeners();
    }

    addSizeFilterDismissListeners() {
      if (!this.sizeFilter || this.sizeFilterDismissListenersAdded) return;

      this.handleSizeFilterOutsideClick ||= (event) => {
        if (this.sizeFilter.open && !this.sizeFilter.contains(event.target)) {
          this.sizeFilter.removeAttribute('open');
        }
      };
      this.handleSizeFilterEscape ||= (event) => {
        if (event.key !== 'Escape' || !this.sizeFilter.open) return;

        this.sizeFilter.removeAttribute('open');
        this.sizeFilter.querySelector('summary')?.focus();
      };
      document.addEventListener('click', this.handleSizeFilterOutsideClick);
      document.addEventListener('keydown', this.handleSizeFilterEscape);
      this.sizeFilterDismissListenersAdded = true;
    }

    removeSizeFilterDismissListeners() {
      if (!this.sizeFilterDismissListenersAdded) return;

      document.removeEventListener('click', this.handleSizeFilterOutsideClick);
      document.removeEventListener('keydown', this.handleSizeFilterEscape);
      this.sizeFilterDismissListenersAdded = false;
    }

    initialize() {
      this.sortLists();
      this.loadCounts();
    }

    sizeUrlValue(id) {
      return String(id).split('/').pop();
    }

    restoreControlsFromUrl() {
      if (!window.location?.href) return;

      const params = new URL(window.location.href).searchParams;
      this.urlSizeValues = new Set(params.getAll('size'));

      const sortByAvailable = params.get('sort') === 'available';
      if (this.sortSelect) {
        this.sortSelect.setAttribute('aria-pressed', String(sortByAvailable));
        this.sortSelect.value = sortByAvailable
          ? this.sortSelect.dataset.sortActiveValue
          : this.sortSelect.dataset.sortDefaultValue;
      }

      const hideSoldOut = params.get('sold_out') !== 'show';
      if (this.soldOutToggle) {
        this.soldOutToggle.setAttribute('aria-pressed', String(hideSoldOut));
        const label = this.soldOutToggle.querySelector('[data-brand-sold-out-label]');
        if (label) {
          label.textContent = hideSoldOut
            ? this.soldOutToggle.dataset.showLabel
            : this.soldOutToggle.dataset.hideLabel;
        }
      }
    }

    updateUrlState() {
      if (!window.location?.href || !window.history?.replaceState) return;

      const url = new URL(window.location.href);
      url.searchParams.delete('size');
      Array.from(this.selectedSizeIds)
        .map((id) => this.sizeUrlValue(id))
        .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }))
        .forEach((value) => url.searchParams.append('size', value));

      if (this.sortSelect?.value === 'available') {
        url.searchParams.set('sort', 'available');
      } else {
        url.searchParams.delete('sort');
      }

      if (this.soldOutToggle?.getAttribute('aria-pressed') === 'true') {
        url.searchParams.delete('sold_out');
      } else {
        url.searchParams.set('sold_out', 'show');
      }

      window.history.replaceState(window.history.state, '', url);
    }

    directoryPageUrl() {
      const url = new URL(this.dataset.directoryUrl, window.location.origin);
      url.searchParams.set('view', 'brand-directory-data');
      return url;
    }

    async fetchDirectoryPage(url, expectedPage, signal) {
      let lastError;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const response = await fetch(url, {
            credentials: 'same-origin',
            cache: 'no-store',
            signal,
          });
          if (!response.ok) {
            throw new Error(`Brand directory page ${expectedPage} failed: ${response.status} ${response.url}`);
          }

          const payload = await response.json();
          if (!Number.isInteger(payload?.page)
            || !Number.isInteger(payload?.pages)
            || payload.page !== expectedPage
            || payload.pages < 1
            || !Array.isArray(payload.brands)
            || (payload.nextUrl !== null && typeof payload.nextUrl !== 'string')) {
            throw new Error('Brand directory response was incomplete');
          }
          payload.brands.forEach((brand) => {
            if (typeof brand?.label !== 'string'
              || !brand.label.trim()
              || (brand.originEmoji !== undefined && typeof brand.originEmoji !== 'string')
              || (brand.markerKind !== undefined && !['country', 'classification', 'region', 'unresolved'].includes(brand.markerKind))
              || (brand.markerCode !== undefined && typeof brand.markerCode !== 'string')
              || (brand.markerLabel !== undefined && typeof brand.markerLabel !== 'string')
              || typeof brand.url !== 'string'
              || !brand.url
              || typeof brand.gid !== 'string'
              || !brand.gid.startsWith('gid://shopify/Metaobject/')) {
              throw new Error('Brand directory item data was incomplete');
            }
          });
          return payload;
        } catch (error) {
          if (signal.aborted) throw error;
          lastError = error;
          if (attempt < 2) {
            await new Promise((resolve) => window.setTimeout(resolve, 400 * (2 ** attempt)));
          }
        }
      }

      throw lastError;
    }

    async loadAllBrandPages() {
      const totalPages = Number(this.dataset.totalPages || 1);
      const currentPage = Number(this.dataset.currentPage || 1);
      if (totalPages <= 1) {
        this.updateAlphabetAvailability();
        return;
      }

      const cachedPages = this.readSessionCache('pages');
      if (cachedPages?.totalPages === totalPages && Array.isArray(cachedPages.payloads)) {
        this.mergeBrandPages(cachedPages.payloads);
        this.updateAlphabetAvailability();
        this.parentElement?.querySelector('[data-brand-pagination]')?.remove();
        return;
      }

      const request = new AbortController();
      this.directoryRequest = request;
      const payloads = [];
      let page = 1;
      let nextUrl = this.directoryPageUrl();
      if (currentPage === 1 && this.dataset.nextPageUrl) {
        page = 2;
        nextUrl = new URL(this.dataset.nextPageUrl, window.location.origin);
        nextUrl.searchParams.set('view', 'brand-directory-data');
      }

      for (; page <= totalPages; page += 1) {
        const payload = await this.fetchDirectoryPage(nextUrl, page, request.signal);
        payloads.push(payload);
        nextUrl = payload.nextUrl ? new URL(payload.nextUrl, window.location.origin) : null;
        if (page < totalPages && !nextUrl) {
          throw new Error('Brand directory pagination ended early');
        }
      }
      if (request.signal.aborted || this.directoryRequest !== request) return;
      if (payloads.some((payload) => payload.pages !== totalPages)) {
        throw new Error('Brand directory pagination changed during loading');
      }

      this.writeSessionCache('pages', { totalPages, payloads });
      this.mergeBrandPages(payloads);
      this.updateAlphabetAvailability();
      this.parentElement?.querySelector('[data-brand-pagination]')?.remove();
    }

    mergeBrandPages(payloads) {
      const groupsContainer = this.querySelector('.brands-directory__groups');
      const groupTemplate = this.querySelector('[data-brand-group]');
      const itemTemplate = this.querySelector('[data-brand-item]');
      if (!groupsContainer || !groupTemplate || !itemTemplate) return;

      const existingUrls = new Set(Array.from(this.querySelectorAll('[data-brand-item]'))
        .map((item) => new URL(item.dataset.brandUrl, window.location.origin).pathname));

      payloads.flatMap((payload) => payload.brands).forEach((brand) => {
        const brandPath = new URL(brand.legacyUrl || brand.url, window.location.origin).pathname;
        if (existingUrls.has(brandPath)) return;

        const label = brand.label.trim();
        const initial = label.charAt(0).toUpperCase();
        const groupKey = /^[A-Z]$/u.test(initial) ? initial.toLowerCase() : 'other';
        let group = this.querySelector(`#brands-${groupKey}`);
        if (!group) {
          group = groupTemplate.cloneNode(true);
          group.id = `brands-${groupKey}`;
          group.hidden = false;
          group.open = true;
          group.querySelector('.brands-directory__group-heading > span:first-child').textContent = groupKey === 'other'
            ? '#'
            : groupKey.toUpperCase();
          group.querySelector('[data-brand-list]').replaceChildren();
          groupsContainer.append(group);
        }

        const item = itemTemplate.cloneNode(true);
        item.hidden = false;
        item.dataset.brandLabel = label.toLocaleLowerCase();
        item.dataset.brandUrl = brandPath;
        item.dataset.brandProductsUrl = brand.url;
        item.dataset.brandFilter = brand.gid;
        item.dataset.brandAvailable = '-1';
        item.dataset.brandTotal = '-1';
        item.brandSizeIds = new Set();
        item.classList.add('is-count-loading');
        item.classList.remove('is-count-error', 'is-sold-out');
        const link = item.querySelector('.brands-directory__brand-link');
        link.href = brand.url;
        link.replaceChildren();
        if (brand.originEmoji || brand.markerAssetUrl) {
          const originFlag = document.createElement('span');
          const markerKind = brand.markerKind || 'country';
          originFlag.className = `brand-origin-flag brand-marker brand-marker--${markerKind}`;
          originFlag.setAttribute('role', 'img');
          originFlag.setAttribute('aria-label', brand.markerLabel || 'Brand marker');
          originFlag.title = brand.markerLabel || '';
          if (markerKind === 'country') {
            originFlag.dataset.originCountry = brand.markerCode || '';
          } else {
            originFlag.dataset.brandClassification = brand.markerCode || '';
          }
          if ((markerKind === 'country' || markerKind === 'region') && brand.markerAssetUrl) {
            const flagImage = document.createElement('img');
            flagImage.className = 'country-flag-image country-emoji brand-origin-flag__image';
            flagImage.src = brand.markerAssetUrl;
            flagImage.alt = '';
            flagImage.setAttribute('aria-hidden', 'true');
            flagImage.width = 18;
            flagImage.height = 18;
            flagImage.loading = 'lazy';
            originFlag.append(flagImage);
          } else {
            originFlag.textContent = brand.originEmoji;
          }
          link.append(originFlag);
        }
        link.append(label);
        item.querySelector('[data-brand-available-count]').textContent = '';
        item.querySelector('[data-brand-total-count]').textContent = '';
        group.querySelector('[data-brand-list]').append(item);
        existingUrls.add(brandPath);
      });

      const groupOrder = 'abcdefghijklmnopqrstuvwxyz#';
      const groups = Array.from(groupsContainer.querySelectorAll('[data-brand-group]'));
      groups.sort((left, right) => {
        const leftKey = left.id === 'brands-other' ? '#' : left.id.replace('brands-', '');
        const rightKey = right.id === 'brands-other' ? '#' : right.id.replace('brands-', '');
        return groupOrder.indexOf(leftKey) - groupOrder.indexOf(rightKey);
      });
      groupsContainer.append(...groups);
    }

    updateAlphabetAvailability() {
      this.querySelectorAll('[data-brand-group-link]').forEach((link) => {
        const groupExists = Boolean(this.querySelector(`#brands-${link.dataset.brandGroupLink}`));
        link.classList.toggle('is-disabled', !groupExists);
        link.toggleAttribute('aria-disabled', !groupExists);
        if (groupExists) {
          link.href = `#brands-${link.dataset.brandGroupLink}`;
          link.removeAttribute('tabindex');
        } else {
          link.removeAttribute('href');
          link.setAttribute('tabindex', '-1');
        }
      });
    }

    countUrl(page) {
      const url = new URL(this.dataset.collectionUrl, window.location.origin);
      url.searchParams.set('view', 'brand-counts');
      if (page > 1) url.searchParams.set('page', String(page));
      return url;
    }

    async fetchCountPage(page, signal) {
      let lastError;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const response = await fetch(this.countUrl(page), {
            credentials: 'same-origin',
            cache: 'no-store',
            signal,
          });

          if (!response.ok) {
            throw new Error(`Brand counts page ${page} failed: ${response.status} ${response.url}`);
          }

          const payload = await response.json();
          if (!Number.isInteger(payload?.page)
            || !Number.isInteger(payload?.pages)
            || payload.page !== page
            || payload.pages < 1
            || payload.page > payload.pages
            || !Array.isArray(payload.products)) {
            throw new Error('Brand counts response was incomplete');
          }

          payload.products.forEach((product) => {
            if (typeof product?.id !== 'string'
              || !product.id.startsWith('gid://shopify/Product/')
              || typeof product?.brandUrl !== 'string'
              || !product.brandUrl
              || typeof product.available !== 'boolean'
              || (product.sizeIds !== undefined
                && (!Array.isArray(product.sizeIds)
                  || product.sizeIds.some((id) => typeof id !== 'string')))) {
              throw new Error('Brand counts product data was incomplete');
            }
          });

          return payload;
        } catch (error) {
          if (signal.aborted) throw error;
          lastError = error;
          if (attempt < 2) {
            await new Promise((resolve) => window.setTimeout(resolve, 400 * (2 ** attempt)));
          }
        }
      }

      throw lastError;
    }

    async collectCountPages(signal) {
      const firstPage = await this.fetchCountPage(1, signal);
      const pages = [firstPage];
      const queue = Array.from(
        { length: firstPage.pages - 1 },
        (_, index) => index + 2
      );
      const workerCount = Math.min(COUNT_CONCURRENCY, queue.length);
      const workers = Array.from({ length: workerCount }, async () => {
        while (queue.length) {
          const page = queue.shift();
          pages.push(await this.fetchCountPage(page, signal));
        }
      });

      await Promise.all(workers);
      if (pages.length !== firstPage.pages
        || new Set(pages.map((page) => page.page)).size !== firstPage.pages
        || pages.some((page) => page.pages !== firstPage.pages)) {
        throw new Error('Brand counts pagination was incomplete');
      }
      return pages;
    }

    aggregateCounts(pages) {
      const counts = new Map();
      pages.forEach((page) => {
        page.products.forEach((product) => {
          const brandUrl = new URL(product.brandUrl, window.location.origin).pathname;
          const count = counts.get(brandUrl) || {
            available: 0,
            total: 0,
            sizeIds: new Set(),
            productIds: new Set(),
            products: [],
          };
          if (count.productIds.has(product.id)) return;

          const productSizeIds = new Set(product.sizeIds || []);
          count.productIds.add(product.id);
          count.total += 1;
          if (product.available) count.available += 1;
          productSizeIds.forEach((id) => count.sizeIds.add(id));
          count.products.push({ id: product.id, available: product.available, sizeIds: productSizeIds });
          counts.set(brandUrl, count);
        });
      });
      return counts;
    }

    sizeCatalogUrl() {
      const url = new URL(this.dataset.directoryUrl, window.location.origin);
      url.searchParams.set('view', 'brand-size-data');
      return url;
    }

    async collectSizeCatalog(signal) {
      let lastError;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const response = await fetch(this.sizeCatalogUrl(), {
            credentials: 'same-origin',
            cache: 'no-store',
            signal,
          });
          if (!response.ok) throw new Error(`Brand size catalog failed: ${response.status}`);

          const payload = await response.json();
          if (payload?.page !== 1
            || payload?.pages !== 1
            || !Array.isArray(payload.sizes)
            || payload.sizes.some((size) => typeof size?.id !== 'string'
              || !size.id.startsWith('gid://shopify/Metaobject/')
              || typeof size.label !== 'string'
              || !size.label.trim())) {
            throw new Error('Brand size catalog response was incomplete');
          }

          return new Map(payload.sizes.map((size) => [size.id, size.label.trim()]));
        } catch (error) {
          if (signal.aborted) throw error;
          lastError = error;
          if (attempt < 2) {
            await new Promise((resolve) => window.setTimeout(resolve, 400 * (2 ** attempt)));
          }
        }
      }

      throw lastError;
    }

    productSizeIds(product) {
      if (product.sizeRecommend?.type !== 'list.metaobject_reference'
        || typeof product.sizeRecommend.value !== 'string') return [];

      try {
        const ids = JSON.parse(product.sizeRecommend.value);
        if (!Array.isArray(ids)) return [];
        return ids.filter((id) => typeof id === 'string'
          && id.startsWith('gid://shopify/Metaobject/'));
      } catch (_error) {
        return [];
      }
    }

    storefrontToken() {
      const element = document.getElementById('shopify-features');
      if (!element) throw new Error('Shopify storefront features were not found');
      const features = JSON.parse(element.textContent);
      if (typeof features.accessToken !== 'string' || !features.accessToken) {
        throw new Error('Shopify public Storefront token was not found');
      }
      return features.accessToken;
    }

    async fetchStorefrontPage(cursor, token, signal) {
      let lastError;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const response = await fetch(new URL(STOREFRONT_ENDPOINT, window.location.origin), {
            method: 'POST',
            credentials: 'same-origin',
            cache: 'no-store',
            signal,
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': token,
            },
            body: JSON.stringify({
              query: this.dataset.sizeMetafieldKey === 'ai_size_recommend'
                ? STOREFRONT_QUERY.replace('key: "size_recommend"', 'key: "ai_size_recommend"')
                : STOREFRONT_QUERY,
              variables: {
                cursor,
                country: window.Shopify?.country || 'KR',
              },
            }),
          });
          if (!response.ok) {
            const error = new Error(`Storefront counts request failed: ${response.status}`);
            error.retryable = response.status === 429 || response.status >= 500;
            throw error;
          }

          const payload = await response.json();
          if (Array.isArray(payload?.errors) && payload.errors.length) {
            throw new Error('Storefront counts returned GraphQL errors');
          }
          const products = payload?.data?.products;
          if (!Array.isArray(products?.nodes)
            || typeof products?.pageInfo?.hasNextPage !== 'boolean'
            || (products.pageInfo.hasNextPage && typeof products.pageInfo.endCursor !== 'string')) {
            throw new Error('Storefront counts response was incomplete');
          }
          return products;
        } catch (error) {
          if (signal.aborted) throw error;
          lastError = error;
          if (error.retryable === false || attempt === 2) break;
          await new Promise((resolve) => window.setTimeout(resolve, 400 * (2 ** attempt)));
        }
      }

      throw lastError;
    }

    async collectStorefrontCounts(signal) {
      const token = this.storefrontToken();
      const counts = new Map();
      const productIds = new Set();
      const cursors = new Set();
      let cursor = null;

      for (let page = 0; page < 20; page += 1) {
        const products = await this.fetchStorefrontPage(cursor, token, signal);
        products.nodes.forEach((product) => {
          if (typeof product?.id !== 'string'
            || typeof product.availableForSale !== 'boolean'
            || productIds.has(product.id)) return;
          productIds.add(product.id);

          const brandGid = product.brand?.value;
          if (product.brand?.type !== 'metaobject_reference'
            || typeof brandGid !== 'string'
            || !brandGid.startsWith('gid://shopify/Metaobject/')) return;
          const count = counts.get(brandGid) || {
            available: 0,
            total: 0,
            sizeIds: new Set(),
            products: [],
          };
          const productSizeIds = new Set(this.productSizeIds(product));
          count.total += 1;
          if (product.availableForSale) count.available += 1;
          productSizeIds.forEach((id) => count.sizeIds.add(id));
          count.products.push({ id: product.id, available: product.availableForSale, sizeIds: productSizeIds });
          counts.set(brandGid, count);
        });

        if (!products.pageInfo.hasNextPage) return counts;
        const nextCursor = products.pageInfo.endCursor;
        if (!nextCursor || cursors.has(nextCursor)) {
          throw new Error('Storefront counts cursor did not advance');
        }
        cursors.add(nextCursor);
        cursor = nextCursor;
      }

      throw new Error('Storefront counts exceeded the page safety limit');
    }

    async collectCounts(signal) {
      try {
        return {
          keyType: 'gid',
          counts: await this.collectStorefrontCounts(signal),
        };
      } catch (error) {
        if (signal.aborted) throw error;
        console.warn('Storefront counts unavailable; using Liquid fallback.', error);
        const pages = await this.collectCountPages(signal);
        return {
          keyType: 'url',
          counts: this.aggregateCounts(pages),
        };
      }
    }

    applyCounts(result) {
      this.items.forEach((item) => {
        const key = result.keyType === 'gid'
          ? item.dataset.brandFilter
          : new URL(
            item.dataset.brandUrl || item.querySelector('.brands-directory__brand-link').href,
            window.location.origin
          ).pathname;
        const count = result.counts.get(key) || {
          available: 0,
          total: 0,
          sizeIds: new Set(),
          products: [],
        };
        item.dataset.brandAvailable = String(count.available);
        item.dataset.brandTotal = String(count.total);
        item.brandSizeIds = new Set(count.sizeIds || []);
        item.brandProducts = Array.from(count.products || []);
        item.classList.remove('is-count-loading', 'is-count-error');
        item.classList.toggle('is-sold-out', count.available === 0);
        item.querySelector('[data-brand-available-count]').textContent = String(count.available);
        item.querySelector('[data-brand-total-count]').textContent = String(count.total);
      });
      this.updateDisplayedCounts();
      this.updateBrandLinks();
    }

    buildSizeFilter(result, sizeCatalog) {
      if (!this.sizeFilter || !this.sizeOptions || !this.sizeCount || !this.sizeClear) return;

      const referencedIds = new Set();
      const productCounts = new Map();
      result.counts.forEach((count) => {
        (count.sizeIds || []).forEach((id) => referencedIds.add(id));
        (count.products || []).forEach((product) => {
          (product.sizeIds || []).forEach((id) => {
            productCounts.set(id, (productCounts.get(id) || 0) + 1);
          });
        });
      });
      const options = Array.from(sizeCatalog.entries())
        .filter(([id]) => referencedIds.has(id))
        .sort((left, right) => left[1].localeCompare(right[1], undefined, {
          numeric: true,
          sensitivity: 'base',
        }));

      this.sizeLabels = new Map(options);
      this.sizeOptions.replaceChildren(...options.map(([id, label]) => {
        const option = document.createElement('label');
        option.className = 'brands-directory__size-option';
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.value = id;
        checkbox.checked = this.selectedSizeIds.has(id)
          || this.urlSizeValues?.has(this.sizeUrlValue(id));
        const text = document.createElement('span');
        text.textContent = `${label} (${productCounts.get(id) || 0})`;
        option.append(checkbox, text);
        return option;
      }));

      this.sizeFilter.hidden = options.length === 0;
      this.updateSizeSelection();
    }

    updateSizeSelection() {
      if (!this.sizeOptions) return;
      this.selectedSizeIds = new Set(Array.from(
        this.sizeOptions.querySelectorAll('input[type="checkbox"]:checked')
      ).map((checkbox) => checkbox.value));
      if (this.sizeCount) {
        this.sizeCount.textContent = String(this.selectedSizeIds.size);
        this.sizeCount.hidden = this.selectedSizeIds.size === 0;
      }
      if (this.sizeClear) this.sizeClear.disabled = this.selectedSizeIds.size === 0;
      this.updateDisplayedCounts();
      this.updateActiveFilterBadges();
      this.updateBrandLinks();
      this.updateSoldOutVisibility();
      this.updateUrlState();
    }

    updateActiveFilterBadges() {
      if (!this.activeFilters) return;
      const badges = Array.from(this.selectedSizeIds).map((id) => {
        const badge = document.createElement('button');
        badge.className = 'brands-directory__filter-badge';
        badge.type = 'button';
        badge.dataset.brandSizeBadge = id;
        const label = this.sizeLabels?.get(id) || this.sizeUrlValue(id);
        badge.textContent = `${label} ×`;
        badge.setAttribute('aria-label', `${label} 필터 제거`);
        return badge;
      });
      this.activeFilters.replaceChildren(...badges);
      this.activeFilters.hidden = badges.length === 0;
    }

    updateBrandLinks() {
      this.items.forEach((item) => {
        const link = item.querySelector('.brands-directory__brand-link');
        if (!link) return;
        const url = new URL(item.dataset.brandProductsUrl || link.href, window.location.origin);
        const sizeFilterParam = this.dataset.sizeFilterParam || 'filter.p.m.custom.size_recommend';
        url.searchParams.delete(sizeFilterParam);
        Array.from(this.selectedSizeIds)
          .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }))
          .forEach((id) => url.searchParams.append(sizeFilterParam, id));
        link.href = url;
      });
    }

    productMatchesSelectedSize(product) {
      return Array.from(this.selectedSizeIds)
        .some((id) => product.sizeIds?.has(id));
    }

    selectedAvailableCount(item) {
      return (item.brandProducts || []).filter((product) => product.available
        && this.productMatchesSelectedSize(product)).length;
    }

    updateDisplayedCounts() {
      const hasSizeFilter = this.selectedSizeIds.size > 0;
      this.items.forEach((item) => {
        const available = Number(item.dataset.brandAvailable);
        const total = Number(item.dataset.brandTotal);
        if (available < 0 || total < 0) return;

        const filteredTotal = hasSizeFilter
          ? this.selectedAvailableCount(item)
          : available;
        item.querySelector('[data-brand-available-count]').textContent = String(filteredTotal);
        item.querySelector('[data-brand-total-count]').textContent = String(total);

        const counts = item.querySelector('[data-brand-counts]');
        if (counts) {
          counts.title = hasSizeFilter
            ? counts.dataset.filteredTitle
            : counts.dataset.defaultTitle;
        }
      });
    }

    clearSizeSelection() {
      this.sizeOptions?.querySelectorAll('input[type="checkbox"]:checked')
        .forEach((checkbox) => { checkbox.checked = false; });
      this.updateSizeSelection();
    }

    showCountError() {
      this.items.forEach((item) => {
        item.dataset.brandAvailable = '-1';
        item.dataset.brandTotal = '-1';
        item.classList.remove('is-count-loading', 'is-sold-out');
        item.classList.add('is-count-error');
        item.querySelector('[data-brand-available-count]').textContent = this.dataset.errorLabel;
        item.querySelector('[data-brand-total-count]').textContent = this.dataset.errorLabel;
      });
    }

    async loadCounts() {
      this.countRequest?.abort();
      const request = new AbortController();
      this.countRequest = request;
      this.setAttribute('aria-busy', 'true');
      this.setCountControlsEnabled(false);

      try {
        const cached = this.deserializeCountResult(this.readSessionCache('counts'));
        let result;
        let sizeCatalog;
        if (cached) {
          ({ result, sizeCatalog } = cached);
        } else {
          const sizeCatalogPromise = this.collectSizeCatalog(request.signal).catch((error) => {
            if (!request.signal.aborted) console.error(error);
            return new Map();
          });
          [result, sizeCatalog] = await Promise.all([
            this.collectCounts(request.signal),
            sizeCatalogPromise,
          ]);
          this.writeSessionCache('counts', this.serializeCountResult(result, sizeCatalog));
        }
        if (this.countRequest !== request || request.signal.aborted) return;

        this.applyCounts(result);
        this.buildSizeFilter(result, sizeCatalog);
        this.setCountControlsEnabled(true);
        if (this.sortSelect?.value === 'available') this.sortLists();
        this.updateSoldOutVisibility();

        const directoryPromise = this.loadAllBrandPages().catch((error) => {
          console.error(error);
        });
        await directoryPromise;
        if (this.countRequest !== request || request.signal.aborted) return;
        this.items = Array.from(this.querySelectorAll('[data-brand-item]'));
        this.sortLists();
        this.applyCounts(result);
        if (this.sortSelect?.value === 'available') this.sortLists();
        this.updateSoldOutVisibility();
      } catch (error) {
        if (this.countRequest !== request || request.signal.aborted) return;
        request.abort();
        this.showCountError();
        console.error(error);
      } finally {
        if (this.countRequest === request) this.setAttribute('aria-busy', 'false');
      }
    }

    setCountControlsEnabled(enabled) {
      if (this.sortSelect) this.sortSelect.disabled = !enabled;
      if (this.soldOutToggle) this.soldOutToggle.disabled = !enabled;
    }

    toggleSortMode() {
      if (!this.sortSelect || this.sortSelect.disabled) return;

      const isActive = this.sortSelect.getAttribute('aria-pressed') === 'true';
      this.sortSelect.setAttribute('aria-pressed', String(!isActive));
      this.sortSelect.value = isActive
        ? this.sortSelect.dataset.sortDefaultValue
        : this.sortSelect.dataset.sortActiveValue;
      this.sortLists();
      this.updateUrlState();
    }

    toggleSoldOutBrands() {
      if (!this.soldOutToggle || this.soldOutToggle.disabled) return;

      const shouldHide = this.soldOutToggle.getAttribute('aria-pressed') !== 'true';
      this.soldOutToggle.setAttribute('aria-pressed', String(shouldHide));
      const label = this.soldOutToggle.querySelector('[data-brand-sold-out-label]');
      if (label) {
        label.textContent = shouldHide
          ? this.soldOutToggle.dataset.showLabel
          : this.soldOutToggle.dataset.hideLabel;
      }
      this.updateSoldOutVisibility();
      this.updateUrlState();
    }

    updateSoldOutVisibility() {
      const shouldHide = this.soldOutToggle?.getAttribute('aria-pressed') === 'true';
      const hasSizeFilter = this.selectedSizeIds.size > 0;
      this.items.forEach((item) => {
        const matchesSize = !hasSizeFilter || Array.from(this.selectedSizeIds)
          .some((id) => item.brandSizeIds?.has(id));
        const selectedSizeSoldOut = hasSizeFilter && this.selectedAvailableCount(item) === 0;
        item.hidden = (shouldHide
          && (item.classList.contains('is-sold-out') || selectedSizeSoldOut)) || !matchesSize;
      });

      let visibleCount = 0;
      this.querySelectorAll('[data-brand-group]').forEach((group) => {
        const visibleItems = Array.from(group.querySelectorAll('[data-brand-item]'))
          .filter((item) => !item.hidden);
        visibleCount += visibleItems.length;
        group.hidden = (shouldHide || hasSizeFilter) && visibleItems.length === 0;

        const groupId = group.id.replace(/^brands-/, '');
        const groupLink = this.querySelector(`[data-brand-group-link="${groupId}"]`);
        if (groupLink) groupLink.hidden = group.hidden;
      });
      if (this.filterEmpty) this.filterEmpty.hidden = !hasSizeFilter || visibleCount > 0;
    }

    sortLists() {
      const mode = this.sortSelect?.value || 'alphabetical';

      this.querySelectorAll('[data-brand-list]').forEach((list) => {
        const items = Array.from(list.querySelectorAll('[data-brand-item]'));
        items.sort((left, right) => {
          if (mode === 'available') {
            const countDifference = Number(right.dataset.brandAvailable) - Number(left.dataset.brandAvailable);
            if (countDifference !== 0) return countDifference;
          }

          return left.dataset.brandLabel.localeCompare(right.dataset.brandLabel, undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        });
        list.append(...items);
      });
    }
  }

  customElements.define('brand-directory', BrandDirectory);
})();
